/**
 * 错误处理工具函数
 */

/**
 * 从错误对象中提取错误消息
 *
 * 适配统一响应格式（RFC-135）：
 * - error.message: 后端返回的错误消息（由响应拦截器提取）
 * - error.code: HTTP 状态码
 * - error.data: 错误详情（如 422 的 validation errors）
 */
export function getErrorMessage(error: unknown, fallback = '操作失败，请稍后重试'): string {
  if (!error) return fallback;

  // 标准 Error 对象
  if (error instanceof Error) {
    // 优先从 data.errors 提取具体校验错误
    const detailMsg = _extractFirstErrorDetail(error);
    if (detailMsg) return detailMsg;
    return error.message || fallback;
  }

  // 字符串错误
  if (typeof error === 'string') {
    return error;
  }

  // 对象错误（兼容旧格式）
  if (typeof error === 'object') {
    const err = error as any;

    // 优先从 data.errors 提取具体校验错误
    const detailMsg = _extractFirstErrorDetail(err);
    if (detailMsg) return detailMsg;

    // 使用 message
    if (err.message) {
      return err.message;
    }

    // 兼容旧格式：response.data.detail
    if (err.response?.data?.detail) {
      return err.response.data.detail;
    }

    // 兼容旧格式：response.data.message
    if (err.response?.data?.message) {
      return err.response.data.message;
    }
  }

  return fallback;
}

/**
 * 从 data.errors 中提取第一条具体错误信息
 * 适配 model_validator 返回的 {"field": "", "message": "..."} 格式
 */
function _extractFirstErrorDetail(error: any): string | null {
  const data = error?.data ?? error?.response?.data;
  const errors = data?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const first = errors[0];
  if (typeof first === 'string') return first;
  if (typeof first?.message === 'string') return first.message;
  if (typeof first?.msg === 'string') return first.msg;
  return null;
}

/**
 * 获取错误状态码
 */
export function getErrorCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;

  const err = error as any;

  // 新格式：error.code
  if (typeof err.code === 'number') {
    return err.code;
  }

  // 兼容旧格式：error.response.status
  if (typeof err.response?.status === 'number') {
    return err.response.status;
  }

  return undefined;
}

/** 需要用户处理的请求错误；超时和限流仍允许自动恢复。 */
export function isNonRetryableHttpError(error: unknown): boolean {
  const code = getErrorCode(error);
  return code !== undefined && code >= 400 && code < 500 && code !== 408 && code !== 429;
}

/**
 * 获取错误详情数据
 *
 * 用于获取 422 参数验证错误的详细信息
 */
export function getErrorData(error: unknown): any {
  if (!error || typeof error !== 'object') return null;

  const err = error as any;

  // 新格式：error.data
  if (err.data) {
    return err.data;
  }

  // 兼容旧格式：error.response.data
  if (err.response?.data) {
    return err.response.data;
  }

  return null;
}

/**
 * 获取业务错误码（兼容顶层 errorCode 与 data.code）。
 */
export function getBusinessErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const err = error as any;
  const topLevel = err.errorCode ?? err.response?.data?.errorCode;
  if (typeof topLevel === 'string') return topLevel;
  const data = getErrorData(error);
  const nested = data?.code ?? data?.errorCode ?? err.response?.data?.data?.code;
  return typeof nested === 'string' ? nested : undefined;
}

export function isAccountStatusError(error: unknown): boolean {
  const code = getBusinessErrorCode(error);
  return (
    code === 'ACCOUNT_BANNED' || code === 'ACCOUNT_SUSPENDED' || code === 'ACCOUNT_DEACTIVATED'
  );
}

/**
 * 判断是否为认证错误（需要清除 token）
 * 仅 401 需要清除 token，403 是权限不足但仍是登录状态
 */
export function isAuthError(error: unknown): boolean {
  const code = getErrorCode(error);
  return code === 401;
}

/**
 * 判断是否为权限不足错误。
 *
 * 覆盖 HTTP 403 以及统一响应里的业务 403，供全局 toast 与组件 catch 去重使用。
 */
export function isPermissionDeniedError(error: unknown): boolean {
  return getErrorCode(error) === 403;
}

export function getPermissionDeniedMessage(error?: unknown): string {
  const message = error ? getErrorMessage(error, '') : '';
  return message && message !== '请求失败' ? message : '当前账号暂无执行该操作的权限';
}

export function shouldSuppressLocalErrorToast(error: unknown): boolean {
  return isPermissionDeniedError(error);
}

/**
 * 判断是否为参数验证错误
 */
export function isValidationError(error: unknown): boolean {
  return getErrorCode(error) === 422;
}

/**
 * 判断是否为限流错误
 */
export function isRateLimitError(error: unknown): boolean {
  return getErrorCode(error) === 429;
}

/**
 * 格式化验证错误消息
 *
 * 将 422 错误的 errors 数组格式化为可读的消息
 */
export function formatValidationErrors(error: unknown): string[] {
  if (!isValidationError(error)) return [];

  const data = getErrorData(error);
  if (!data?.errors || !Array.isArray(data.errors)) return [];

  return data.errors.map((err: any) => {
    const field = err.loc?.join(' -> ') || '未知字段';
    const msg = err.msg || '验证失败';
    return `${field}: ${msg}`;
  });
}

/**
 * 将任意类型的 API 错误值规范化为可读字符串，防止对象直接进入 toast 或 Error.message。
 *
 * 后端偶尔会在统一响应的 message 字段中携带 { message, errors } 结构（如参数校验失败），
 * 直接传给 React toast 会触发 React #31（对象不是合法子节点）。
 * 此函数是前端的最终兜底，即使后端已修复契约，此处也保证安全。
 */
export function normalizeApiErrorMessage(value: unknown): string {
  if (typeof value === 'string') return value || '请求失败';
  if (value instanceof Error) return value.message || '请求失败';
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const msg = obj['message'];
    if (typeof msg === 'string' && msg) {
      const errors = obj['errors'];
      if (Array.isArray(errors) && errors.length > 0) {
        const firstErr = errors[0];
        const detail =
          typeof firstErr === 'string'
            ? firstErr
            : typeof firstErr === 'object' && firstErr !== null
              ? String((firstErr as Record<string, unknown>)['message'] ?? '')
              : '';
        return detail ? `${msg}：${detail}` : msg;
      }
      return msg;
    }
  }
  return '请求失败，请稍后重试';
}
